/**
 * Onboarding (cold-start) question flow.
 *
 * Shown to new shoppers with no Neo4j presence. Collects stated preferences via
 * a short, Netflix-style stepper (multi-select tiles with min/max), then POSTs
 * them to /onboard and hands the resulting RecommendationResult back to the
 * parent, which renders it with the existing carousel UI. Only this stepper is
 * new — all results presentation is reused.
 */

import { AlertCircle, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguage } from '../config/siteMode';
import {
  type OnboardingOption,
  type OnboardingQuestion,
  type OnboardRequestBody,
  postOnboarding,
  type RecommendationResult,
} from '../services/api';
import IndigoLogo from './IndigoLogo';
import LoadingScreen from './LoadingScreen';

interface OnboardingFlowProps {
  shopifyCustomerId: string;
  questions: OnboardingQuestion[];
  cdnMode?: boolean;
  customerFirstName?: string;
  /** Called with the blended recommendations once onboarding succeeds. */
  onComplete: (result: RecommendationResult) => void;
  /** Called when onboarding cannot produce results (no shelves) — fall back. */
  onSkip?: () => void;
  /** Test/preview hook: replaces the real POST /onboard call. */
  onSubmit?: (body: OnboardRequestBody) => Promise<RecommendationResult>;
}

type AnswerMap = Record<string, string | string[]>;

// Keep the post-submit loader on screen for a minimum duration (even if the
// blend returns sooner) and advance its rotating messages at this cadence
// (3 messages x ~1666ms ≈ 5s).
const SUBMIT_MIN_LOADING_MS = 5000;
const LOADING_MESSAGE_INTERVAL_MS = 1666;

/** Serve audience each recipient maps to (mirrors backend `_AUDIENCE_MAP`). */
const RECIPIENT_AUDIENCE: Record<string, string> = {
  self: 'ADULT',
  teen: 'TEEN',
  kids: 'KIDS',
  gift: 'ADULT',
};

/** Audiences covered by the selected recipient(s); defaults to adult. */
const audiencesForRecipients = (recipients: string[]): string[] => {
  const audiences = new Set<string>();
  for (const recipient of recipients.length > 0 ? recipients : ['self']) {
    audiences.add(RECIPIENT_AUDIENCE[recipient] ?? 'ADULT');
  }
  return Array.from(audiences);
};

/** The recipient answer is always a multi-select array; guard for the unset case. */
const recipientsFrom = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : [];

/** The kids_age answer is a multi-select array of band ids; guard the unset case. */
const kidsBandsFrom = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : [];

/**
 * Whether a question applies to the chosen recipients. A question with no
 * `applies_to_recipients` is universal; one scoped to (e.g.) `['kids']` only
 * shows when a matching recipient is selected. Keeps the age step out of an
 * adult-only flow.
 */
const questionApplies = (question: OnboardingQuestion, recipients: string[]): boolean => {
  if (!question.applies_to_recipients?.length) return true;
  return question.applies_to_recipients.some((r) => recipients.includes(r));
};

/**
 * Options visible for a question given current answers. Genres are filtered to
 * the union of the selected recipients' audiences so a shopper only sees
 * relevant genres (e.g. no kids' genres when shopping for themselves). Options
 * with no `audiences` tag (older builds) are always shown. KIDS genres that
 * declare `age_bands` are additionally filtered to the shopper's chosen age
 * band(s), so a tile never dead-ends on an empty age-scoped shelf.
 */
const visibleOptions = (
  question: OnboardingQuestion,
  recipients: string[],
  kidsBands: string[]
): OnboardingOption[] => {
  const options = question.options ?? [];
  if (question.id !== 'genres') return options;
  const audiences = audiencesForRecipients(recipients);
  return options.filter((opt) => {
    const audienceOk = !opt.audiences?.length || opt.audiences.some((a) => audiences.includes(a));
    if (!audienceOk) return false;
    if (opt.age_bands?.length && kidsBands.length > 0) {
      return opt.age_bands.some((b) => kidsBands.includes(b));
    }
    return true;
  });
};

/** Seed defaults: pre-select the site language on the languages question. */
function initialAnswers(questions: OnboardingQuestion[]): AnswerMap {
  const answers: AnswerMap = {};
  for (const question of questions) {
    if (question.id === 'languages') {
      answers.languages = [getLanguage()];
    }
  }
  return answers;
}

const selectionCount = (question: OnboardingQuestion, answers: AnswerMap): number => {
  const value = answers[question.id];
  if (question.type === 'multi') return Array.isArray(value) ? value.length : 0;
  return typeof value === 'string' && value.length > 0 ? 1 : 0;
};

const meetsRequirements = (question: OnboardingQuestion, answers: AnswerMap): boolean => {
  const count = selectionCount(question, answers);
  if (question.required && count === 0) return false;
  if (question.min && count < question.min) return false;
  return true;
};

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  shopifyCustomerId,
  questions,
  customerFirstName,
  onComplete,
  onSkip,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>(() => initialAnswers(questions));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recipients = recipientsFrom(answers.recipient_type);
  const recipientKey = recipients.join(',');
  const kidsBands = kidsBandsFrom(answers.kids_age);
  const kidsKey = kidsBands.join(',');

  // Keep only answerable questions: dynamic genre/theme/language/age questions
  // can arrive with an empty option list when no build exists yet, and
  // recipient-scoped steps (e.g. the KIDS age band) only apply when a matching
  // recipient is selected — so the age step is skipped in an adult-only flow.
  const steps = useMemo(
    () =>
      questions.filter(
        (q) =>
          (!q.options || q.options.length > 0) &&
          questionApplies(q, recipientKey ? recipientKey.split(',') : [])
      ),
    [questions, recipientKey]
  );

  const boundedStepIndex = Math.min(stepIndex, Math.max(0, steps.length - 1));
  const question = steps[boundedStepIndex];
  const isLastStep = boundedStepIndex === steps.length - 1;

  // Keep the active step in range when the step list shrinks (e.g. the shopper
  // de-selects "kids", removing the age step).
  useEffect(() => {
    setStepIndex((i) => Math.min(i, Math.max(0, steps.length - 1)));
  }, [steps.length]);

  // Drop any selected genres that aren't valid for the chosen recipient(s) or
  // age band(s) (e.g. after the shopper changes "who are you shopping for?" or
  // their kids' age going back a step).
  useEffect(() => {
    const genreStep = steps.find((q) => q.id === 'genres');
    if (!genreStep) return;
    const audiences = audiencesForRecipients(recipientKey ? recipientKey.split(',') : []);
    const bands = kidsKey ? kidsKey.split(',') : [];
    const allowed = new Set(
      (genreStep.options ?? [])
        .filter((opt) => {
          const audienceOk =
            !opt.audiences?.length || opt.audiences.some((a) => audiences.includes(a));
          if (!audienceOk) return false;
          if (opt.age_bands?.length && bands.length > 0) {
            return opt.age_bands.some((b) => bands.includes(b));
          }
          return true;
        })
        .map((opt) => opt.value)
    );
    setAnswers((prev) => {
      const current = Array.isArray(prev.genres) ? (prev.genres as string[]) : [];
      const next = current.filter((value) => allowed.has(value));
      return next.length === current.length ? prev : { ...prev, genres: next };
    });
  }, [recipientKey, kidsKey, steps]);
  const optionLabel = (option: { value: string; i18n_key?: string; label?: string }): string =>
    option.label ?? (option.i18n_key ? t(option.i18n_key) : option.value);

  const toggleMulti = (value: string) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[question.id]) ? (prev[question.id] as string[]) : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : question.max && current.length >= question.max
          ? current
          : [...current, value];
      return { ...prev, [question.id]: next };
    });
  };

  const selectSingle = (value: string) => {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
  };

  const canProceed = meetsRequirements(question, answers);

  const requirementHint = (): string | null => {
    if (question.type !== 'multi') return null;
    if (question.min && question.min > 1)
      return t('onboarding.selectAtLeast', { min: question.min });
    if (question.max) return t('onboarding.selectUpTo', { max: question.max });
    return t('onboarding.selectAny');
  };

  const handleNext = () => {
    setError(null);
    if (!isLastStep) {
      setStepIndex((i) => i + 1);
    } else {
      void handleSubmit();
    }
  };

  const handleBack = () => {
    setError(null);
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    const startedAt = Date.now();
    const holdForMinLoading = async () => {
      const remaining = SUBMIT_MIN_LOADING_MS - (Date.now() - startedAt);
      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }
    };
    try {
      const languages = (answers.languages as string[] | undefined)?.filter(Boolean) ?? [
        getLanguage(),
      ];
      const body: OnboardRequestBody = {
        recipient_type: recipients.length > 0 ? recipients : ['self'],
        genres: (answers.genres as string[]) || [],
        themes: (answers.themes as string[]) || [],
        languages: languages.length > 0 ? languages : [getLanguage()],
        kids_age: recipients.includes('kids') ? kidsBands : [],
      };
      const result = onSubmit
        ? await onSubmit(body)
        : await postOnboarding(shopifyCustomerId, body);
      const hasCarousels =
        result.recommendations_by_profile &&
        Object.keys(result.recommendations_by_profile).length > 0;
      if (result.status === 'unavailable' || !hasCarousels) {
        await holdForMinLoading();
        onSkip?.();
        return;
      }
      await holdForMinLoading();
      onComplete(result);
    } catch {
      setError(t('onboarding.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (steps.length === 0) {
    onSkip?.();
    return null;
  }

  if (submitting) {
    const loadingMessages = t('onboarding.loadingOnboarding', { returnObjects: true }) as string[];
    return <LoadingScreen messages={loadingMessages} intervalMs={LOADING_MESSAGE_INTERVAL_MS} />;
  }

  const selected = answers[question.id];
  const hint = requirementHint();
  const count = selectionCount(question, answers);

  const renderOptionButton = (option: OnboardingOption) => {
    const isSelected =
      question.type === 'multi'
        ? Array.isArray(selected) && selected.includes(option.value)
        : selected === option.value;
    const onClick = () =>
      question.type === 'multi' ? toggleMulti(option.value) : selectSingle(option.value);

    return (
      <button
        key={option.value}
        type="button"
        onClick={onClick}
        aria-pressed={isSelected}
        className={`relative px-4 py-4 rounded-sm border text-left text-sm transition-colors ${
          isSelected
            ? 'bg-indigo-brand-black text-white border-indigo-brand-black'
            : 'bg-white text-indigo-brand-text border-indigo-brand-border hover:border-indigo-brand-black'
        }`}
      >
        {isSelected && (
          <span className="absolute top-2 right-2">
            <Check className="w-4 h-4" />
          </span>
        )}
        <span className="pr-5 block leading-snug">{optionLabel(option)}</span>
      </button>
    );
  };

  // Group genre tiles under localized audience headers only when shopping for
  // more than one audience (e.g. self + kids), so a single-audience picker stays
  // flat and header-free. Options with no audience tag (older builds) apply to
  // everyone and render first, headerless.
  const options = visibleOptions(question, recipients, kidsBands);
  const groupAudiences = audiencesForRecipients(recipients);
  const groupByAudience = question.id === 'genres' && groupAudiences.length > 1;
  const audienceGroups = groupByAudience
    ? [null, ...groupAudiences].map((audience) => ({
        audience,
        options: options.filter((opt) =>
          audience === null
            ? !opt.audiences?.some((a) => groupAudiences.includes(a))
            : (opt.audiences?.find((a) => groupAudiences.includes(a)) ?? null) === audience
        ),
      }))
    : [];

  return (
    <div className="min-h-screen bg-white flex flex-col font-body">
      <header className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <IndigoLogo />
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 pb-12">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-6">
            <p className="text-xs uppercase tracking-wide text-indigo-brand-text/50 mb-3">
              {t('onboarding.stepOf', { current: boundedStepIndex + 1, total: steps.length })}
            </p>
            <h1 className="text-2xl md:text-4xl font-normal text-indigo-brand-text mb-2 leading-tight">
              {customerFirstName
                ? t('onboarding.greeting', { name: customerFirstName })
                : t('onboarding.title')}
            </h1>
            <p className="text-indigo-brand-text/60">{t('onboarding.subtitle')}</p>
          </div>

          {/* Progress bar */}
          <div className="h-1 w-full bg-indigo-brand-border/50 rounded-full mb-8 overflow-hidden">
            <div
              className="h-full bg-indigo-brand-black transition-all duration-300"
              style={{ width: `${((boundedStepIndex + 1) / steps.length) * 100}%` }}
            />
          </div>

          <div className="bg-white border border-indigo-brand-border rounded-sm p-5 sm:p-8">
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <h2 className="text-lg sm:text-xl text-indigo-brand-text">{t(question.i18n_key)}</h2>
              {question.type === 'multi' && count > 0 && (
                <span className="text-sm text-indigo-brand-text/50 whitespace-nowrap">
                  {t('onboarding.selectedCount', { count })}
                </span>
              )}
            </div>
            {hint && <p className="text-sm text-indigo-brand-text/50 mb-5">{hint}</p>}

            {groupByAudience ? (
              <div className="mt-4 space-y-6">
                {audienceGroups.map(({ audience, options: groupOptions }) =>
                  groupOptions.length === 0 ? null : (
                    <div key={audience ?? 'all'}>
                      {audience && (
                        <h3 className="text-xs uppercase tracking-wide text-indigo-brand-text/50 mb-3">
                          {t(`onboarding.audienceHeader.${audience}`)}
                        </h3>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {groupOptions.map(renderOptionButton)}
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                {options.map(renderOptionButton)}
              </div>
            )}

            {error && (
              <div className="mt-5 flex items-center gap-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={boundedStepIndex === 0 || submitting}
                className="inline-flex items-center gap-1 text-indigo-brand-text/70 disabled:opacity-30 hover:text-indigo-brand-text"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('onboarding.back')}
              </button>

              <div className="flex items-center gap-4">
                {!question.required && onSkip && (
                  <button
                    type="button"
                    onClick={onSkip}
                    disabled={submitting}
                    className="text-indigo-brand-text/50 text-sm hover:text-indigo-brand-text"
                  >
                    {t('onboarding.skip')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!canProceed || submitting}
                  className="inline-flex items-center gap-2 bg-indigo-brand-black text-white px-8 py-3 rounded-full font-medium tracking-wide disabled:opacity-40 hover:bg-black transition-colors"
                >
                  {isLastStep ? t('onboarding.submit') : t('onboarding.next')}
                  {!isLastStep && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingFlow;
